import React, { useState, useCallback } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import ModeSelection from './components/ModeSelection';
import DifficultySelection from './components/DifficultySelection';
import CodeDifficultySelection from './components/CodeDifficultySelection';
import PlateGame from './components/PlateGame';
import CodeGame from './components/CodeGame';
import GameOver from './components/GameOver';
import Version from './components/Version';
import { useLanguage } from './contexts/LanguageContext';
import { shuffleArray } from './utils/gameUtils';

const GAME_STATES = {
  MODE_SELECTION: 'modeSelection',
  DIFFICULTY_SELECTION: 'difficultySelection',
  CODE_DIFFICULTY_SELECTION: 'codeDifficultySelection',
  PLATE_GAME: 'plateGame',
  CODE_GAME: 'codeGame',
  GAME_OVER: 'gameOver'
};

function GameApp() {
  const { t, getCountries } = useLanguage();
  const [gameState, setGameState] = useState(GAME_STATES.MODE_SELECTION);
  const [gameMode, setGameMode] = useState(null);
  const [difficulty, setDifficulty] = useState(null);
  const [gameData, setGameData] = useState({
    score: 0,
    totalAttempts: 0,
    elapsedTime: 0,
    remainingCountries: []
  });

  const selectMode = useCallback((mode) => {
    setGameMode(mode);
    if (mode === 'plate') {
      setGameState(GAME_STATES.DIFFICULTY_SELECTION);
    } else if (mode === 'code') {
      setGameState(GAME_STATES.CODE_DIFFICULTY_SELECTION);
    }
  }, []);

  const selectDifficulty = useCallback((selectedDifficulty) => {
    startGame('plate', selectedDifficulty);
  }, []);

  const selectCodeDifficulty = useCallback((selectedDifficulty) => {
    startGame('code', selectedDifficulty);
  }, []);

  const startGame = useCallback((mode, selectedDifficulty) => {
    setGameMode(mode);
    setDifficulty(selectedDifficulty);
    setGameData({
      score: 0,
      totalAttempts: 0,
      elapsedTime: 0,
      remainingCountries: shuffleArray(getCountries())
    });
    
    if (mode === 'plate') {
      setGameState(GAME_STATES.PLATE_GAME);
    } else {
      setGameState(GAME_STATES.CODE_GAME);
    }
  }, [getCountries]);

  const updateGameData = useCallback((newData) => {
    setGameData(prev => ({ ...prev, ...newData }));
  }, []);

  const showGameOver = useCallback((finalData) => {
    setGameData(prev => ({ ...prev, ...finalData }));
    setGameState(GAME_STATES.GAME_OVER);
  }, []);

  const backToModeSelection = useCallback(() => {
    setGameState(GAME_STATES.MODE_SELECTION);
    setGameMode(null);
    setDifficulty(null);
  }, []);

  const backToDifficultySelection = useCallback(() => {
    setGameState(GAME_STATES.DIFFICULTY_SELECTION);
  }, []);

  const backToCodeDifficultySelection = useCallback(() => {
    setGameState(GAME_STATES.CODE_DIFFICULTY_SELECTION);
  }, []);

  const restartGame = useCallback(() => {
    if (gameMode === 'plate') {
      startGame('plate', difficulty);
    } else {
      startGame('code', difficulty);
    }
  }, [gameMode, difficulty, startGame]);

  return (
    <div className="App">
      <div className="game-container">
        <div className="header-container">
          <div className="title-with-logo">
            <img src="/images/logo/logo.png" alt="PlatePeek Logo" className="title-logo" />
            <h1>{t('title')}</h1>
          </div>
          <LanguageSwitcher />
        </div>
        
        {gameState === GAME_STATES.MODE_SELECTION && (
          <ModeSelection onSelectMode={selectMode} />
        )}
        
        {gameState === GAME_STATES.DIFFICULTY_SELECTION && (
          <DifficultySelection 
            onSelectDifficulty={selectDifficulty}
            onBack={backToModeSelection}
          />
        )}

        {gameState === GAME_STATES.CODE_DIFFICULTY_SELECTION && (
          <CodeDifficultySelection 
            onSelectDifficulty={selectCodeDifficulty}
            onBack={backToModeSelection}
          />
        )}
        
        {gameState === GAME_STATES.PLATE_GAME && (
          <PlateGame
            difficulty={difficulty}
            gameData={gameData}
            onUpdateGameData={updateGameData}
            onGameOver={showGameOver}
            onBack={backToDifficultySelection}
          />
        )}
        
        {gameState === GAME_STATES.CODE_GAME && (
          <CodeGame
            difficulty={difficulty}
            gameData={gameData}
            onUpdateGameData={updateGameData}
            onGameOver={showGameOver}
            onBack={backToCodeDifficultySelection}
          />
        )}
        
        {gameState === GAME_STATES.GAME_OVER && (
          <GameOver
            gameData={gameData}
            gameMode={gameMode}
            difficulty={difficulty}
            onRestart={restartGame}
            onBackToMenu={backToModeSelection}
          />
        )}
        
        <Version />
      </div>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <GameApp />
    </LanguageProvider>
  );
}

export default App;